// Synchronous cache for top-nav data — eliminates load flicker.
// All values are written at login/profile-load/role-load/org-load and
// read synchronously on mount in nav components.

export const NAV_CACHE_KEYS = {
  userName: "compaire_user_name",
  userInitials: "compaire_user_initials",
  userRole: "compaire_user_role", // "admin" | "agent"
  orgName: "compaire_org_name",
  unreadCount: "compaire_unread_count",
} as const;

const safeGet = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key) ?? localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
    localStorage.setItem(key, value);
  } catch {}
};

const safeClear = (key: string) => {
  try {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  } catch {}
};

export const getCachedUserName = () => safeGet(NAV_CACHE_KEYS.userName) ?? "";
export const getCachedUserInitials = () => safeGet(NAV_CACHE_KEYS.userInitials) ?? "";
export const getCachedUserRole = (): "admin" | "agent" | null => {
  const v = safeGet(NAV_CACHE_KEYS.userRole);
  return v === "admin" || v === "agent" ? v : null;
};
export const getCachedOrgName = () => safeGet(NAV_CACHE_KEYS.orgName) ?? "";
export const getCachedUnreadCount = (): number => {
  const v = safeGet(NAV_CACHE_KEYS.unreadCount);
  const n = v ? parseInt(v, 10) : 0;
  return Number.isFinite(n) ? n : 0;
};

export const setCachedUserName = (v: string) => v && safeSet(NAV_CACHE_KEYS.userName, v);
export const setCachedUserInitials = (v: string) => v && safeSet(NAV_CACHE_KEYS.userInitials, v);
export const setCachedUserRole = (v: "admin" | "agent") => safeSet(NAV_CACHE_KEYS.userRole, v);
export const setCachedOrgName = (v: string) => v && safeSet(NAV_CACHE_KEYS.orgName, v);
export const setCachedUnreadCount = (n: number) => safeSet(NAV_CACHE_KEYS.unreadCount, String(n));

/** Forget the cached role and org name, so the next read resolves them fresh. */
export const clearCachedRoleAndOrg = () => {
  safeClear(NAV_CACHE_KEYS.userRole);
  safeClear(NAV_CACHE_KEYS.orgName);
};

export const clearNavCache = () => {
  Object.values(NAV_CACHE_KEYS).forEach(safeClear);
};

export const computeInitials = (name: string): string => {
  if (!name) return "";
  return name
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};
